import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Building2, Users, BedDouble, CalendarPlus, DollarSign, TrendingUp } from "lucide-react";
import { Progress } from "@/components/ui/progress";

function Kpi({ icon: Icon, label, value, tint }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div><p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p><p className="font-display text-3xl font-extrabold mt-2">{value}</p></div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tint}1a`, color: tint }}><Icon className="w-5 h-5" /></div>
      </div>
    </Card>
  );
}

export default function Platform() {
  const { t } = useT();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/platform/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-muted-foreground">{t("loading")}</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-1">{t("platformOverview")}</h1>
      <p className="text-muted-foreground mb-6">Multi-center analytics</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Building2} label={t("activeCenters")} value={`${data.active_centers}/${data.total_centers}`} tint="#3B82F6" />
        <Kpi icon={Users} label={t("totalEmployees")} value={data.total_employees} tint="#8B5CF6" />
        <Kpi icon={BedDouble} label={t("residents")} value={data.total_residents} tint="#10B981" />
        <Kpi icon={DollarSign} label={t("revenue")} value={`$${data.revenue.toLocaleString()}`} tint="#F59E0B" />
      </div>

      <Card className="p-6">
        <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />Centers</h3>
        <div className="space-y-4">
          {data.centers.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-4" data-testid="platform-center-row">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${c.color}1a` }}><Building2 className="w-4 h-4" style={{ color: c.color }} /></div>
              <div className="min-w-40">
                <p className="font-medium flex items-center gap-2">{c.name}
                  {c.status === "suspended" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">{t("suspended")}</span>}
                </p>
                <p className="text-xs text-muted-foreground">{c.employees} {t("employees").toLowerCase()} · {c.residents} {t("residents").toLowerCase()}</p>
              </div>
              <div className="flex-1 min-w-32">
                <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">{t("taskCompletion")}</span><span className="font-medium">{c.task_completion}%</span></div>
                <Progress value={c.task_completion} className="h-2" />
              </div>
              <div className="text-center"><p className="font-display font-bold">{c.open_shifts}</p><p className="text-[10px] text-muted-foreground uppercase">{t("openShifts")}</p></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
